import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ImageUpload } from './ImageUpload/ImageUpload';
import { Settings, Download, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { GenerationFormProps, GenerationSettings } from './GenerationForm/types';
export const GenerationForm: React.FC<GenerationFormProps> = ({
  onGenerate,
  isGenerating,
  onProgressUpdate
}) => {
  const [styleGuidance, setStyleGuidance] = useState('');
  const [selectedImages, setSelectedImages] = useState<Array<{
    file: File;
    keywords: string;
  }>>([]);
  const [settings, setSettings] = useState<GenerationSettings>({
    title: true,
    description: true,
    altText: true,
    descriptionLength: 200
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedImages.length === 0) return;

    // Initialize generation state
    onGenerate([]);
    onProgressUpdate({
      current: 0,
      total: selectedImages.length
    });
    try {
      const results = [];
      for (let i = 0; i < selectedImages.length; i++) {
        const {
          file,
          keywords
        } = selectedImages[i];
        try {
          const reader = new FileReader();
          const imageBase64 = await new Promise<string>(resolve => {
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          });
          const {
            data,
            error
          } = await supabase.functions.invoke('generate-content', {
            body: {
              image: imageBase64,
              keywords,
              styleGuidance,
              settings
            }
          });
          if (error) throw error;
          results.push({
            ...data,
            imageName: file.name
          });

          // Update progress after each successful generation
          onProgressUpdate({
            current: i + 1,
            total: selectedImages.length
          });
        } catch (error) {
          console.error(`Error processing image ${file.name}:`, error);
          toast.error(`Failed to process ${file.name}. Continuing with remaining images...`);
        }
      }
      if (results.length > 0) {
        onGenerate(results);
        toast.success('Content generated successfully!');
      } else {
        toast.error('Failed to generate any content. Please try again.');
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate content. Please try again.');
    }
  };
  const handleSettingChange = (field: keyof GenerationSettings, value: boolean | number) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };
  return <form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-sage-800">Upload Images</h2>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Settings className="w-4 h-4" />
                Settings
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Generation Settings</SheetTitle>
                <SheetDescription>Customize how your image descriptions and alt text are generated.</SheetDescription>
              </SheetHeader>
              <div className="space-y-6 mt-6">
                <div className="space-y-2">
                  <Label>Style Guidance</Label>
                  <Textarea placeholder="Enter any style preferences or brand guidelines" value={styleGuidance} onChange={e => setStyleGuidance(e.target.value)} rows={4} />
                </div>

                <div className="space-y-4">
                  <Label>Content to Generate</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="title" checked={settings.title} onCheckedChange={checked => handleSettingChange('title', checked === true)} />
                      <Label htmlFor="title">Title</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="description" checked={settings.description} onCheckedChange={checked => handleSettingChange('description', checked === true)} />
                      <Label htmlFor="description">Description</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="altText" checked={settings.altText} onCheckedChange={checked => handleSettingChange('altText', checked === true)} />
                      <Label htmlFor="altText">Alt Text</Label>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between">
                    <Label>Description Length</Label>
                    <span className="text-sm text-muted-foreground">
                      {settings.descriptionLength} words
                    </span>
                  </div>
                  <Slider value={[settings.descriptionLength]} onValueChange={([value]) => handleSettingChange('descriptionLength', value)} min={150} max={300} step={10} className="w-full" />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        
        <ImageUpload onImagesSelected={setSelectedImages} />
      </div>

      <div className="flex justify-end gap-4">
        <Button type="submit" className="gap-2 bg-sage-400 hover:bg-sage-500 button-animation" disabled={isGenerating || selectedImages.length === 0}>
          {isGenerating ? <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating
            </> : <>
              <Download className="w-4 h-4" />
              Generate Descriptions
            </>}
        </Button>
      </div>
    </form>;
};